import React, { useState, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import i18next from 'i18next';

import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Skeleton } from 'primereact/skeleton';
import { Avatar } from 'primereact/avatar';

import usePaginationStore from '../../store/usePaginationStore';

const t = (i18nKey, params) => i18next.t(`userDashboard.campaigns.${i18nKey}`, params);

const CampaignTable = ({ data = [], dataCount = 0, dataPages = 1, loading }) => {
    const [selectedRows, setSelectedRows] = useState([]);
    const [selectedCampaignIds, setSelectedCampaignIds] = useState([]);

    const history = useHistory();
    const { resetPagination } = usePaginationStore();

    const isSkeletonRow = (row) => {
        return Object.values(row).some(value => React.isValidElement(value) && value.type === Skeleton);
    };

    const onRowClick = useCallback((e) => {
        if (isSkeletonRow(e.data)) return;
        resetPagination();

        const campaignId = e.data.campaign_id;
        history.push(`/dashboard/campaigns/${campaignId}/candidates`);
    }, []);

    const onSelectionChange = useCallback((e) => {
        const filteredRows = e.value.filter(row => !isSkeletonRow(row));
        setSelectedRows(filteredRows);
        const selectedIds = filteredRows.map(row => row.campaign_id);
        setSelectedCampaignIds(selectedIds);
    }, []);

    const onHeaderClick = useCallback((field) => {
        console.log(`Sorting by ${field}`);
    }, []);

    const headerTemplate = useCallback((options) => (
        <>
            <span onClick={() => onHeaderClick(options.field)} className='is-clickable'>
                {options.header}
            </span>
            <i className="fa-solid fa-chevron-down right-icon"></i>
        </>
    ), [onHeaderClick]);

    const questionBodyTemplate = useCallback((rowData) => {
        return loading ? <Skeleton width="100%" /> : rowData.has_questions ? 'Oui' : 'Non';
    }, [loading]);

    const recruiterBodyTemplate = useCallback((rowData) => {
        if (loading) return <Skeleton width="100%" />;

        return (
            <div className="is-flex is-align-items-center">
                {rowData?.recruiter?.profile_picture ? (
                    <Avatar
                        image={rowData?.recruiter?.profile_picture ? `/dashboard/user/avatar/${rowData?.recruiter?.profile_picture}` : null}
                        className="mr-3 my-1"
                        size="normal"
                        shape="circle"
                    />
                ) : (
                    <div className="deepsy-anonymous-avatar mr-3 my-1">
                        <span className='has-text-dgrad-deepsy'>{rowData.recruiter.first_name[0].toUpperCase()}{rowData.recruiter.last_name[0].toUpperCase()}</span>
                    </div>
                )}

                <span style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{rowData?.recruiter?.display_name || `${rowData?.recruiter?.first_name} ${rowData?.recruiter?.last_name}`}</span>
            </div>
        );
    })

    const statusBodyTemplate = useCallback((rowData) => {
        if (loading) return <Skeleton width="100%" />;

        if (rowData.status === 'enabled') return (<span className='d-success-tag'>Active</span>);

        if (rowData.status === 'paused') return (<span className='d-pause-tag no-wrap'>En pause</span>);

        if (rowData.status === 'disabled') return (<span className='d-inactive-tag'>Inactive</span>);

        return rowData.status;
    }, [loading]);

    const generateSkeletonData = useMemo(() => {
        return Array.from({ length: 10 }, (_, index) => ({
            campaign_id: index,
            campaign_name: <Skeleton width="100%" />,
            language: <Skeleton width="100%" />,
            recruiter: <Skeleton width="100%" />,
            has_questions: <Skeleton width="100%" />,
            status: <Skeleton width="100%" />,
            candidates_count: <Skeleton width="100%" />,
            created_at: <Skeleton width="100%" />
        }));
    }, []);

    const displayData = loading ? generateSkeletonData : data;

    if (!loading && !data.length) return null;

    return (
        <DataTable
            dataKey="campaign_id"
            stripedRows
            sortMode="multiple"
            value={displayData}
            selection={selectedRows}
            onSelectionChange={onSelectionChange}
            selectionMode="checkbox"
            metaKeySelection={false} // Ensures only checkbox clicks select rows
            onRowClick={onRowClick}>

            <Column selectionMode="multiple" headerStyle={{ width: '3em' }} />

            <Column sortable field="campaign_name" header={t('table.0')} body={(rowData) => <strong>{rowData.campaign_name}</strong>} headerTemplate={headerTemplate} />

            <Column sortable field="language" header={t('table.1')} headerTemplate={headerTemplate} />

            <Column sortable field="recruiter" header={t('table.2')} headerTemplate={headerTemplate} body={recruiterBodyTemplate} />

            <Column sortable field="has_questions" header={t('table.3')} body={questionBodyTemplate} headerTemplate={headerTemplate} />

            <Column sortable field="status" header={t('table.4')} body={statusBodyTemplate} headerTemplate={headerTemplate} />

            <Column sortable field="candidates_count" header={t('table.5')} headerTemplate={headerTemplate} />

            <Column sortable field="created_at" header={t('table.6')} headerTemplate={headerTemplate} />
        </DataTable>
    );
};

export default CampaignTable;